import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Product } from './index';
import { categoryService } from '../../services/categoryService';
import { productService } from '../../services/productService';
import DefaultMetaTags from '../../components/DefaultMetaTags';
import LastVisitedItems from './LastVisitedItems';
import logo from '../../assets/images/logo-blue.png';
import './Shop.css';

const Shop: React.FC = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const categoriesRef = useRef<HTMLDivElement>(null);

  // Get category from URL parameter
  const getCategoryFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('category');
  }, [location.search]);

  const [activeCategory, setActiveCategory] = useState<string | null>(getCategoryFromUrl());
  const [products, setProducts] = useState<Product[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Set current language (Arabic or English)
  const currentLanguage = language === 'ar' ? 'ar' : 'en';

  // Get meta info for the current category
  const getCategoryMetaInfo = () => {
    if (!activeCategory) {
      return {
        title: language === 'ar' ? ' متجر الأكاديمية للأدوات العلمية والتعليمية' : 'Academy Shop',
        description: language === 'ar'
          ? 'تسوق أدواتنا التعليمية المميزة لتعزيز تعلم العلوم والتكنولوجيا والهندسة والرياضيات.'
          : 'Shop our premium educational tools to enhance learning in science, technology, engineering, and mathematics.'
      };
    }

    const categoryName = categoryService.getCategoryName(activeCategory, currentLanguage as 'en' | 'ar');
    return {
      title: categoryName,
      description: language === 'ar'
        ? `تصفح أدواتنا  في فئة ${categoryName} - أدوات تعليمية عالية الجودة لتعزيز تعلم الأطفال والشباب.`
        : `Browse our ${categoryName} tools - high-quality educational tools to enhance learning for children and youth.`
    };
  };

  const { title, description } = getCategoryMetaInfo();

  // Load sample products for demo purposes when API fails
  const loadSampleProducts = useCallback(() => {
    // Sample products categorized according to new categories
    const sampleProducts: Product[] = [
      // Kids Science
      {
        id: 1,
        titleEn: "Kids First Chemistry Lab",
        titleAr: "معمل الكيمياء الأول للأطفال",
        price: 199,
        descriptionEn: "A safe introduction to chemistry with 25 fun experiments designed specifically for young scientists. Includes non-toxic chemicals, real lab equipment, and an illustrated guidebook.",
        descriptionAr: "مقدمة آمنة للكيمياء مع 25 تجربة ممتعة مصممة خصيصًا للعلماء الصغار. تتضمن مواد كيميائية غير سامة، معدات مختبرية حقيقية، ودليل مصور.",
        ageRangeEn: "8-12 years",
        ageRangeAr: "8-12 سنة",
        category: "kids_science",
        images: [
          { id: 1, url: "/api/placeholder/400/300", alt: "Chemistry kit", isMain: true },
          { id: 2, url: "/api/placeholder/400/300", alt: "Experiment setup" }
        ],
        featured: true
      },
      {
        id: 2,
        titleEn: "Solar System Explorer Kit",
        titleAr: "مجموعة استكشاف النظام الشمسي",
        price: 149,
        descriptionEn: "Learn about planets, stars, and space with this comprehensive astronomy kit. Features a telescope, planetarium projector, and detailed star charts.",
        descriptionAr: "تعلم عن الكواكب والنجوم والفضاء مع هذه المجموعة الشاملة لعلم الفلك. تتضمن تلسكوب وجهاز عرض القبة السماوية وخرائط نجوم مفصلة.",
        ageRangeEn: "10+ years",
        ageRangeAr: "10+ سنوات",
        category: "kids_science",
        images: [
          { id: 3, url: "/api/placeholder/400/300", alt: "Solar system kit", isMain: true }
        ],
        featured: true
      },
      // Add more sample products with some featured...
    ];

    // Store in localStorage for persistence
    localStorage.setItem('sampleProducts', JSON.stringify(sampleProducts));

    return sampleProducts;
  }, []);

  // Function to fetch products from the API
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productService.getAllProducts();
      setProducts(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products. Please try again later.');

      // Load sample products for development/demo purposes
      const sampleProducts = loadSampleProducts();
      setProducts(sampleProducts);
    } finally {
      setLoading(false);
    }
  }, [loadSampleProducts]);

  // Function to fetch featured products
  const fetchFeaturedProducts = useCallback(async () => {
    try {
      console.log('Fetching featured products from API...');

      // First try to get featured products from the dedicated API
      const featuredData = await productService.getFeaturedProducts();
      console.log('Featured products API response:', featuredData);

      if (featuredData && Array.isArray(featuredData) && featuredData.length > 0) {
        console.log(`Found ${featuredData.length} featured products from API`);
        setFeaturedProducts(featuredData);
        return;
      }

      console.log('No featured products from API, checking all products...');

      // If no featured products from API, filter from all products as fallback
      if (products && products.length > 0) {
        const featuredFromAll = products.filter(product => product.featured === true);
        console.log(`Found ${featuredFromAll.length} featured products from all products filter`);
        setFeaturedProducts(featuredFromAll);
      } else {
        console.log('No products available to filter from');
        setFeaturedProducts([]);
      }

    } catch (err) {
      console.error('Error fetching featured products:', err);

      // Final fallback: filter featured from all products
      if (products && products.length > 0) {
        const featuredFromAll = products.filter(product => product.featured === true);
        console.log(`Fallback: Found ${featuredFromAll.length} featured products from all products`);
        setFeaturedProducts(featuredFromAll);
      } else {
        setFeaturedProducts([]);
      }
    }
  }, [products]);

  // Update active category from URL whenever the location changes
  useEffect(() => {
    const categoryFromUrl = getCategoryFromUrl();
    setActiveCategory(categoryFromUrl);
  }, [location.search, getCategoryFromUrl]);

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Fetch featured products when no category is selected AND products are loaded
  useEffect(() => {
    if (!activeCategory && !loading) {
      console.log('No active category and not loading, fetching featured products...');
      fetchFeaturedProducts();
    }
  }, [activeCategory, loading, fetchFeaturedProducts]);

  useEffect(() => {
    // Run this logic ONLY when loading is complete and we have an active category
    if (!loading && activeCategory && categoriesRef.current) {
      const activeItem = categoriesRef.current.querySelector(
        `[data-category-id="${activeCategory}"]`
      ) as HTMLElement;

      if (activeItem) {
        // The timeout ensures the UI has fully updated after the loading state change
        setTimeout(() => {
          activeItem.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
          });
        }, 100);
      }
    }
  }, [activeCategory, loading]);

  // Handle category click - update both state and URL
  const handleCategoryClick = (categoryId: string): void => {
    // Update state
    setActiveCategory(categoryId);

    // Update URL without reloading the page
    const params = new URLSearchParams(location.search);
    if (categoryId) {
      params.set('category', categoryId);
    } else {
      params.delete('category');
    }

    // Use navigate to update URL
    navigate({
      pathname: location.pathname,
      search: params.toString()
    });
  };

  // Get the products to display based on active category
  const getDisplayProducts = (): Product[] => {
    if (activeCategory) {
      return products.filter((product: Product) => product.category === activeCategory);
    }
    return [];
  };

  const displayProducts = getDisplayProducts();

  // Handle shop home click - clear category to show featured products
  const handleShopHomeClick = (): void => {
    // Clear category
    setActiveCategory(null);

    // Clear URL parameters
    navigate({
      pathname: location.pathname,
      search: ''
    });
  };

  // Scroll the categories
  const scrollCategories = (direction: 'left' | 'right') => {
    if (categoriesRef.current) {
      const scrollAmount = 320; // Approximately 2 category items
      const currentScroll = categoriesRef.current.scrollLeft;

      // Calculate new scroll position
      const newScroll = direction === 'left'
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount;

      // Smooth scroll to the new position
      categoriesRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });

      // Update active dot after scrolling
      setTimeout(() => {
        if (categoriesRef.current) {
          // Pagination dot update logic could go here
        }
      }, 300);
    }
  };

  // Handle category container scroll
  const handleCategoryScroll = useCallback(() => {
    if (categoriesRef.current) {
      // Scroll handling logic could go here
    }
  }, []);

  // Add scroll event listener
  useEffect(() => {
    const currentRef = categoriesRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleCategoryScroll);
      return () => {
        currentRef.removeEventListener('scroll', handleCategoryScroll);
      };
    }
  }, [handleCategoryScroll]);

  return (
    <section id="shop" className="shop-section">
      {/* Add meta tags for SEO and social sharing */}
      <DefaultMetaTags
        title={title}
        description={description}
        url={`https://www.aiaj.xyz${location.pathname}${location.search}`}
      />

      <div className="shop-container">
        {/* Logo Section */}
        <div className="shop-logo-container">
          <img src={logo} alt="AI Academy Logo" className="shop-logo" />
        </div>

        <h2 className="section-title">
          <span className="ar-content">{language === 'ar' ? 'متجر الأكاديمية للأدوات العلمية والتعليمية' : ''}</span>
          <span className="en-content">{language === 'en' ? 'Academy Educational Tools' : ''}</span>
        </h2>

        {/* Loading state */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">
              <span className="ar-content">{language === 'ar' ? 'جاري التحميل ...' : ''}</span>
              <span className="en-content">{language === 'en' ? 'Loading products...' : ''}</span>
            </p>
          </div>
        )}

        {/* Error state */}
        {error && products.length === 0 && (
          <div className="error-container">
            <p className="error-message">
              <span className="ar-content">{language === 'ar' ? 'حدث خطأ في التحميل. يرجى المحاولة مرة أخرى لاحقًا.' : ''}</span>
              <span className="en-content">{language === 'en' ? 'An error occurred while loading products. Please try again later.' : ''}</span>
            </p>
            <button className="retry-button" onClick={fetchProducts}>
              <span className="ar-content">{language === 'ar' ? 'إعادة المحاولة' : ''}</span>
              <span className="en-content">{language === 'en' ? 'Retry' : ''}</span>
            </button>
          </div>
        )}

        {/* Desktop Category Filter - Only visible on desktop */}
        {!loading && (
          <div className="shop-categories desktop-only">
            {/* Shop Home Button */}
            <div
              className={`category-item ${!activeCategory ? 'active' : ''}`}
              onClick={handleShopHomeClick}
            >
              <div className="category-icon">🏠</div>
              <div className="category-name">
                <span className="ar-content">{language === 'ar' ? 'الرئيسية' : ''}</span>
                <span className="en-content">{language === 'en' ? 'Shop Home' : ''}</span>
              </div>
            </div>

            {/* Category Items */}
            {categoryService.getAllCategories(currentLanguage as 'en' | 'ar').map(category => (
              <div
                key={category.id}
                className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category.id)}
              >
                <div className="category-icon">{category.icon}</div>
                <div className="category-name">{category.name}</div>
              </div>
            ))}
          </div>
        )}

        {/* Mobile Category Filter - Only visible on mobile */}
        {!loading && (
          <div className="mobile-category-slider-container">
            <button
              className="scroll-indicator left"
              onClick={() => scrollCategories('left')}
              aria-label={language === 'ar' ? 'التمرير لليمين' : 'Scroll left'}
            >
              ❮
            </button>

            <div
              className="shop-categories mobile-only"
              ref={categoriesRef}
            >
              {/* Shop Home Button - Mobile */}
              <div
                className={`category-item ${!activeCategory ? 'active' : ''}`}
                onClick={handleShopHomeClick}
              >
                <div className="category-icon">🏠</div>
                <div className="category-name">
                  <span className="ar-content">{language === 'ar' ? 'الرئيسية' : ''}</span>
                  <span className="en-content">{language === 'en' ? 'Shop Home' : ''}</span>
                </div>
              </div>

              {/* Category Items - Mobile */}
              {categoryService.getAllCategories(currentLanguage as 'en' | 'ar').map(category => (
                <div
                  key={category.id}
                  data-category-id={category.id}
                  className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <div className="category-icon">{category.icon}</div>
                  <div className="category-name">{category.name}</div>
                </div>
              ))}
            </div>

            <button
              className="scroll-indicator right"
              onClick={() => scrollCategories('right')}
              aria-label={language === 'ar' ? 'التمرير لليسار' : 'Scroll right'}
            >
              ❯
            </button>
          </div>
        )}

        {/* Last Visited Items - Show only when no category is selected */}
        {!loading  && (
          <LastVisitedItems />
        )}

        {/* Featured Products - Show only when no category is selected */}
        {!loading && !activeCategory && featuredProducts.length > 0 && (
          <div className="latest-category-products">
            <h3>

            </h3>

            <div className="category-products-grid">
              {featuredProducts.map(product => (
                <Link
                  to={`/shop/product/${product.id}`}
                  key={product.id}
                  className="product-card category-card"
                >
                  <div className="product-image">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images.find(img => img.isMain)?.url || product.images[0].url}
                        alt={product.images[0].alt}
                      />
                    ) : (
                      <div className="placeholder-image">No Image</div>
                    )}
                    <div className="category-badge">
                      {categoryService.getCategoryName(product.category, currentLanguage as 'en' | 'ar')}
                    </div>
                  </div>
                  <div className="product-info">
                    <h3>{language === 'ar' ? product.titleAr : product.titleEn}</h3>
                    <p className="product-price">{product.price} ₪</p>
                    <p className="product-age-range">
                      <span className="ar-content">{language === 'ar' ? `العمر: ${product.ageRangeAr}` : ''}</span>
                      <span className="en-content">{language === 'en' ? `Age: ${product.ageRangeEn}` : ''}</span>
                    </p>
                    <div className="view-details-button">
                      {language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* All Products Grid - Filtered by category if one is selected */}
        {!loading && activeCategory && (
          <div className="products-section">
            <h3>
              <span className="ar-content">
                {language === 'ar' ? categoryService.getCategoryNameAr(activeCategory) : ''}
              </span>
              <span className="en-content">
                {language === 'en' ? categoryService.getCategoryNameEn(activeCategory) : ''}
              </span>
            </h3>

            <div className="products-grid">
              {displayProducts.map((product: Product) => (
                <Link
                  to={`/shop/product/${product.id}`}
                  key={product.id}
                  className="product-card"
                >
                  <div className="product-image">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images.find(img => img.isMain)?.url || product.images[0].url}
                        alt={product.images[0].alt}
                      />
                    ) : (
                      <div className="placeholder-image">No Image</div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3>{language === 'ar' ? product.titleAr : product.titleEn}</h3>
                    <p className="product-price">{product.price} ₪</p>
                    <p className="product-age-range">
                      {language === 'ar' ? product.ageRangeAr : product.ageRangeEn}
                    </p>
                    <div className="view-details-button">
                      {language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* No Products Message */}
            {displayProducts.length === 0 && (
              <div className="no-products-message">
                <span className="ar-content">
                  {language === 'ar' ? 'لا توجد أدوات في هذه الفئة' : ''}
                </span>
                <span className="en-content">
                  {language === 'en' ? 'No tools found in this category' : ''}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Shop;